import React from 'react';
import ReactDOM from 'react-dom/client';
import { ensureSecureRequestUrl } from './config/api';
import App from './App';

// Guard fetch on HTTPS: fix http:// bases, trailing slashes, and 307 Location: http:// from nginx/FastAPI.
const nativeFetch = window.fetch.bind(window);

function resolveFetchUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') {
    return ensureSecureRequestUrl(input);
  }
  if (input instanceof Request) {
    return ensureSecureRequestUrl(input.url);
  }
  if (input instanceof URL) {
    return ensureSecureRequestUrl(input.href);
  }
  return '';
}

function headerRecordFromInit(
  input: RequestInfo | URL,
  init?: RequestInit
): Record<string, string> {
  const out: Record<string, string> = {};
  if (input instanceof Request) {
    input.headers.forEach((value, key) => {
      out[key] = value;
    });
  }
  const incoming = init?.headers;
  if (incoming instanceof Headers) {
    incoming.forEach((value, key) => {
      out[key] = value;
    });
  } else if (Array.isArray(incoming)) {
    for (const [key, value] of incoming) {
      out[key] = value;
    }
  } else if (incoming && typeof incoming === 'object') {
    Object.assign(out, incoming as Record<string, string>);
  }
  return out;
}

window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  const secureUrl = resolveFetchUrl(input);
  const headers = headerRecordFromInit(input, init);
  const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();

  const buildRequest = (): RequestInfo | URL => {
    if (typeof input === 'string') {
      return secureUrl;
    }
    if (input instanceof Request) {
      return secureUrl !== input.url ? new Request(secureUrl, input) : input;
    }
    if (input instanceof URL) {
      return new URL(secureUrl);
    }
    return input;
  };

  let response = await nativeFetch(buildRequest(), {
    ...init,
    headers,
    redirect: 'manual',
  });

  if (
    typeof window !== 'undefined' &&
    window.location.protocol === 'https:' &&
    method === 'GET' &&
    response.status >= 300 &&
    response.status < 400
  ) {
    const location = response.headers.get('Location');
    if (location) {
      const followUrl = ensureSecureRequestUrl(
        location.startsWith('http') ? location : new URL(location, window.location.origin).href
      );
      response = await nativeFetch(followUrl, {
        ...init,
        method: 'GET',
        headers,
        redirect: 'follow',
      });
    }
  }

  return response;
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
